import React from 'react';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {useCurrentUserState} from 'app/current-user/api';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {ActionButton} from 'app/general/structures/SPPage';
import {useLocalStateFilters} from 'app/hooks';
import {useTeams} from 'app/teams/api';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {TEAMS_CREATE_PAGE_URL} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';

import {TeamListItem} from './TeamListItem';
import {TeamsDashboardFilterbar} from './TeamsDashboardFilterbar';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  TeamsFetchParamsType,
  TeamsLocationParamsType,
} from 'app/teams/types';
import type {WithRouterProps} from 'react-router';
import type {TeamSerializedType} from 'sweet-types';

export function TeamsDashboard(
  routerProps: WithRouterProps<unknown, TeamsLocationParamsType>
) {
  const {is_super_manager} = useCurrentUserState().data!;

  return (
    <DashboardPage<
      TeamSerializedType,
      TeamsLocationParamsType,
      TeamsFetchParamsType
    >
      {...routerProps}
      title={gt('Teams')}
      actionArea={
        is_super_manager ? (
          <ActionButton
            size="sm"
            text={gt('Create Team')}
            icon="fa-plus-circle"
            link={TEAMS_CREATE_PAGE_URL}
          />
        ) : null
      }
      useDataHook={useTeams}
      useFiltersHook={useLocalStateFilters<
        TeamsFetchParamsType,
        TeamsLocationParamsType
      >(convertLocationParamsToFetchParams)}
      filterbar={({resetFilters, viewingAll}) => (
        <TeamsDashboardFilterbar
          resetFilters={resetFilters}
          viewingAll={viewingAll}
        />
      )}
      renderItem={(team) => (
        <TeamListItem key={team.id} team={team} hideMembershipStatus={true} />
      )}
      messages={{
        counter: (count) => ngt('%s team', '%s teams', count),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon="fa-users"
              head={gt("There aren't any teams yet")}
            >
              {(FullHTMLLink, urls) =>
                is_super_manager ? (
                  <FullHTMLLink
                    className="btn btn-primary"
                    html_url={urls.TEAMS_CREATE_PAGE_URL}
                  >
                    {gt('Create a new team')}
                  </FullHTMLLink>
                ) : null
              }
            </NoResultsMessage>
          ) : (
            <NoResultsMessage icon="fa-users" head={gt('No teams found')}>
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                {gt('Reset filters and view all teams')}
              </button>
            </NoResultsMessage>
          ),
      }}
    />
  );
}

export const TeamsDashboardBreadcrumb = (
  props: BreadcrumbProps
): JSX.Element => (
  <BreadcrumbItemStructure {...props} icon="fa-users" text="Teams" />
);
